/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import PubSub from 'pubsub-js';
import { queryCache } from 'react-query';
import { buffers, eventChannel, Task } from 'redux-saga';
import {
  ActionPattern,
  all,
  call,
  cancel,
  debounce,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { DefaultLanguage, Language } from 'shared';

import {
  getCommonEventKey,
  getLogoutNoticeEventKey,
  getOrganizationInvitationEventKey,
} from '../constants/notificationsKeys';
import { NOTISTACK_MAX_STACK, PUB_SUB_APP_CHANNEL } from '../constants/settings';
import { i18n, Namespaces } from '../i18n';
import { AuthenticationApiService } from '../services/AuthenticationApiService';
import { socketClient } from '../socket';
import { AppEvents, AppServerEvents, AppStandaloneEvents } from '../types/common/AppEvents';
import { PubSubEvent, PubSubEvents } from '../types/common/PubSubEvents';
import { IUser } from '../types/user/IUser';
import { uiLanguage, userNotifications } from '../utils/storageHelpers';
import { authorizeUserByToken, userLogout } from './auth/actions';
import { AuthActions, AuthLoginAction, AuthLogoutAction, AuthState } from './auth/types';
import { CreateClusterActions, OpenCreateClusterOverlayAction } from './createCluster/types';
import { RootState } from './index';
import { addNotification, setNotifications } from './notifications/actions';
import { INotification, NotificationsActions, NotificationsState } from './notifications/types';
import { enqueueHUSnackbar, enqueueHUSnackbarByEvent } from './notifier/actions';
import { fetchOrganizations } from './organization/actions';
import { getPlanByProduct } from './plan/actions';
import { fetchProjectsByUser } from './projects/actions';
import { OpenUpdateClusterOverlayAction, UpdateClusterActions } from './updateCluster/types';

function* loadNotifications(user: IUser) {
  try {
    const notifications: INotification<AppEvents>[] = yield call([userNotifications, 'get'], user.id);
    if (notifications && notifications.length) {
      yield put(setNotifications(notifications));
    }
  } catch (e) {
    yield call([userNotifications, 'remove'], user.id);
  }
}

function* connectSocket() {
  try {
    yield call([socketClient, 'connect']);
  } catch (e) {
    /* empty */
  }
}

function* disconnectSocket() {
  try {
    yield call([socketClient, 'disconnect']);
  } catch (e) {
    /* empty */
  }
}

function* reactToUserLogout() {
  yield fork(disconnectSocket);
  yield call([queryCache, 'clear']);
}

function* processUserOnLogin(user: IUser) {
  yield call(loadNotifications, user);
  yield delay(4000);
  const { invitations } = user;
  const events: INotification<AppServerEvents.OrganizationInvitation>[] = invitations.map(i => {
    return {
      id: getOrganizationInvitationEventKey(i.id),
      type: AppServerEvents.OrganizationInvitation,
      createdAt: i.createdAt,
      event: {
        invitationId: i.id,
        organization: i.organization,
        user: {
          id: '',
          firstName: '',
          lastName: '',
        },
      },
    };
  });
  const notifications: NotificationsState = yield select((state: RootState) => state.notifications);
  if (events.length) {
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const notificationExists = notifications.options.findIndex(o => o.id === event.id) > -1;
      if (!notificationExists) {
        yield put(addNotification(event));
      }
    }
    if (events.length <= NOTISTACK_MAX_STACK) {
      for (let i = 0; i < events.length; i++) {
        yield put(enqueueHUSnackbarByEvent(events[i]));
      }
    } else {
      const event: INotification<AppStandaloneEvents.SeeInvitations> = {
        type: AppStandaloneEvents.SeeInvitations,
        id: getCommonEventKey(),
        event: { count: events.length },
        createdAt: new Date(),
      };
      yield put(enqueueHUSnackbarByEvent(event, { variant: 'info' }));
    }
  }
}

function* loginFlowSaga() {
  while (true) {
    const action: AuthLoginAction = yield take(AuthActions.AUTH_LOGIN);
    const { user } = action.payload;
    const processUserTask: Task = yield fork(processUserOnLogin, user);
    const logoutAction: AuthLogoutAction = yield take(AuthActions.AUTH_LOGOUT);
    const { isBySystem } = logoutAction.payload;
    yield cancel(processUserTask);
    if (isBySystem) {
      yield put(
        enqueueHUSnackbar(
          {
            primaryText: i18n.t(`${Namespaces.Common}:notices.your_session_expired`).toString(),
          },
          {
            variant: 'info',
            preventDuplicate: true,
            key: getLogoutNoticeEventKey(),
          },
        ),
      );
    } else {
      window.location.replace('/');
    }
  }
}

function* reactToFetchUserSuccess(): Generator {
  yield all([
    yield put(fetchProjectsByUser()),
    yield put(fetchOrganizations()),
    yield fork(connectSocket),
    yield fork(setUILanguage),
  ]);
}

function* setUILanguage() {
  const user: IUser = yield select((state: RootState) => state.auth.user);
  let language: Language;
  if (user) {
    language = user.language;
  } else {
    language = yield call([uiLanguage, 'get']);
  }
  language = language || DefaultLanguage;
  yield call([uiLanguage, 'set'], language);
  void i18n.changeLanguage(language);
}

function* reactToNotAuthorized(): Generator {
  yield all([yield fork(setUILanguage)]);
}

function* reactToOpenCreateClusterOverlay(action: OpenCreateClusterOverlayAction) {
  const { serviceType } = action.payload;
  yield put(getPlanByProduct(serviceType));
}

function* reactToOpenUpdateCluserOverlay(action: OpenUpdateClusterOverlayAction) {
  const { serviceType } = action.payload;
  yield put(getPlanByProduct(serviceType));
}

function* reactToNotificationsAction() {
  const auth: AuthState = yield select((state: RootState) => state.auth);
  const notifications: NotificationsState = yield select((state: RootState) => state.notifications);
  const { user, isAuthenticated } = auth;
  if (user && isAuthenticated) {
    userNotifications.set(user.id, notifications.options);
  }
}

const createPubSubChannel = () =>
  eventChannel<PubSubEvent<PubSubEvents>>(emit => {
    const handler = (_channel: string, event: PubSubEvent<PubSubEvents>) => {
      emit(event);
    };
    const token = PubSub.subscribe(PUB_SUB_APP_CHANNEL, handler);
    return () => {
      PubSub.unsubscribe(token);
    };
  }, buffers.expanding<PubSubEvent<PubSubEvents>>());

function* initPubSubSubscription() {
  const channel: ActionPattern = yield call(createPubSubChannel);
  while (true) {
    const event: PubSubEvent<PubSubEvents> = yield take(channel);
    switch (event.type) {
      case PubSubEvents.RequestUnauthorized: {
        try {
          const result: Awaited<ReturnType<typeof AuthenticationApiService.refresh>> = yield call([
            AuthenticationApiService,
            'refresh',
          ]);
          yield put(authorizeUserByToken(result.data.payload.accessToken, new Date(result.data.payload.expires)));
        } catch (error) {
          yield put(userLogout(true));
        }
        break;
      }
    }
  }
}

export function* appSaga(): Generator {
  yield all([
    yield takeLatest([AuthActions.AUTH_SET_USER, AuthActions.AUTH_CHECK_AUTHORIZED], reactToFetchUserSuccess),
    yield takeLatest(AuthActions.AUTH_CHECK_NOT_AUTHORIZED, reactToNotAuthorized),
    yield takeLatest(AuthActions.AUTH_LOGOUT, reactToUserLogout),
    yield takeLatest(CreateClusterActions.OPEN_CREATE_CLUSTER_OVERLAY, reactToOpenCreateClusterOverlay),
    yield takeLatest(UpdateClusterActions.OPEN_UPDATE_CLUSTER_OVERLAY, reactToOpenUpdateCluserOverlay),
    yield debounce(500, Object.keys(NotificationsActions), reactToNotificationsAction),
    yield fork(initPubSubSubscription),
    yield fork(loginFlowSaga),
  ]);
}
